import Carregando from '../components/Carregando';
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, MenuItem, Alert,
} from '@mui/material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import AcessoNegado from '../components/AcessoNegado';
import ContratosCarga from '../components/contratos/ContratosCarga';
import CargaMensalProjetos from '../components/CargaMensalProjetos';
import { formatDias } from '../utils/formatters';

const NOMES_MES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const RISCO_CONFIG = {
  ritmo:   { label: 'No ritmo', cor: '#16A34A' },
  atencao: { label: 'Atenção',  cor: '#D97706' },
  critico: { label: 'Crítico',  cor: '#DC2626' },
};

function formatarReal(valor) {
  return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// O valor do contrato só chega para quem tem acesso (Controle de Carga, Projetos, Admin).
// Vazio = o Comercial ainda não informou (projetos antigos).
const temContrato = (p) => p.valor_contrato !== null && p.valor_contrato !== undefined && p.valor_contrato !== '';

// RECEBIDO vem do Financeiro: só conta NF com status "Recebido" (NF emitida e não paga não entra).
// recebido_por_mes agrupa pelo mês da data de recebimento: { "2026-09": 12345.0 }.
const recebidoAcumulado = (p) => Number(p.recebido_total || 0);
const recebidoNoMes = (p, mes, ano) => Number((p.recebido_por_mes || {})[`${ano}-${String(mes).padStart(2, '0')}`] || 0);
const pctDoContrato = (valor, p) => (temContrato(p) && Number(p.valor_contrato) > 0 ? (valor / Number(p.valor_contrato)) * 100 : 0);

function calcularRisco(projeto) {
  const pctConsultoria = projeto.carga_total_contratada > 0
    ? (Number(projeto.realizado_acumulado) / Number(projeto.carga_total_contratada)) * 100
    : 0;
  const pctWorkshop = projeto.workshops_vendidos > 0
    ? (Number(projeto.workshops_realizados) / Number(projeto.workshops_vendidos)) * 100
    : 0;
  const pior = Math.max(pctConsultoria, pctWorkshop);
  let nivel = 'ritmo';
  if (pior >= 100) nivel = 'critico';
  else if (pior >= 80) nivel = 'atencao';
  return { pctConsultoria, pctWorkshop, pior, nivel };
}

function AnelGrande({ pct, label, cor }) {
  const pctExibido = Math.min(Math.round(pct), 100);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{
        width: 110, height: 110, borderRadius: '50%', flexShrink: 0,
        background: `conic-gradient(${cor} ${pctExibido * 3.6}deg, rgba(255,255,255,0.14) 0deg)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Box sx={{
          width: 90, height: 90, borderRadius: '50%', backgroundColor: '#0d2642',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
        }}>
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 24, fontFamily: 'monospace', lineHeight: 1 }}>
            {Math.round(pct)}%
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
    </Box>
  );
}

function ContratoPorProjeto({ projetos, mes, ano }) {
  const comValor = projetos.filter(temContrato).sort((a, b) => Number(b.valor_contrato) - Number(a.valor_contrato));
  const semValor = projetos.length - comValor.length;
  return (
    <Card sx={{ mb: 3, borderRadius: 3 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 2 }}>Contrato × andamento por projeto</Typography>

        {comValor.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nenhum projeto com valor de contrato informado neste filtro.</Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {comValor.map((p) => {
              const cfg = RISCO_CONFIG[p.risco.nivel];
              const pctCarga = p.risco.pctConsultoria;
              const recebido = recebidoAcumulado(p);
              const pctRecebido = pctDoContrato(recebido, p);
              return (
                <Box component="li" key={p.id} sx={{ py: 1.4, borderTop: '1px solid #eef0f3', '&:first-of-type': { borderTop: 'none', pt: 0 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1a1a2e' }}>{p.nome}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>{p.cliente_nome}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, fontFamily: 'monospace', color: '#13375F' }}>{formatarReal(p.valor_contrato)}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>valor total do contrato</Typography>
                    </Box>
                  </Box>

                  {[
                    { rotulo: 'Carga realizada', pct: pctCarga, cor: '#e94560' },
                    { rotulo: 'Recebido', pct: pctRecebido, cor: '#16A34A' },
                  ].map((b) => (
                    <Box key={b.rotulo} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.7 }}>
                      <Typography sx={{ fontSize: 11, color: '#94a3b8', width: 92, flexShrink: 0 }}>{b.rotulo}</Typography>
                      <Box sx={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: '#eef0f3', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${Math.min(b.pct, 100)}%`, backgroundColor: b.cor, borderRadius: 4 }} />
                      </Box>
                      <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: '#1a1a2e', width: 40, textAlign: 'right' }}>{Math.round(b.pct)}%</Typography>
                    </Box>
                  ))}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.7, flexWrap: 'wrap', gap: 1 }}>
                    <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                      Recebido em {NOMES_MES[mes - 1].toLowerCase()}: <strong>{formatarReal(recebidoNoMes(p, mes, ano))}</strong>
                      {' · '}Acumulado: <strong>{formatarReal(recebido)}</strong>
                      {' · '}A receber do contrato: <strong>{formatarReal(Math.max(Number(p.valor_contrato) - recebido, 0))}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: cfg.cor }}>● {cfg.label}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {semValor > 0 && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#BA7517' }}>
            {semValor} {semValor === 1 ? 'projeto está' : 'projetos estão'} sem valor de contrato e não entra{semValor === 1 ? '' : 'm'} na lista. O Comercial ou o Financeiro podem informar.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function BarraDistribuicaoRisco({ resumo, total }) {
  return (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 2 }}>Distribuição de risco</Typography>
        {Object.entries(RISCO_CONFIG).map(([chave, cfg]) => {
          const qtd = resumo[chave] || 0;
          const pct = total > 0 ? (qtd / total) * 100 : 0;
          return (
            <Box key={chave} sx={{ mb: 1.8 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a2e' }}>{cfg.label}</Typography>
                <Typography sx={{ fontSize: 12.5, fontFamily: 'monospace', color: '#64748b' }}>{qtd}</Typography>
              </Box>
              <Box sx={{ height: 8, borderRadius: 4, backgroundColor: '#eef0f3', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${pct}%`, backgroundColor: cfg.cor, borderRadius: 4, transition: 'width 0.3s ease' }} />
              </Box>
            </Box>
          );
        })}
        {total === 0 && (
          <Typography variant="caption" color="text.secondary">Nenhum projeto no filtro atual.</Typography>
        )}
      </CardContent>
    </Card>
  );
}

function BarraComparacaoCelulas({ dados }) {
  const maxPct = Math.max(1, ...dados.map((d) => Math.max(d.pctConsultoria, d.pctWorkshop)));
  return (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 2 }}>Comparar células</Typography>
        {dados.length === 0 ? (
          <Typography variant="caption" color="text.secondary">Nenhuma célula com dado nesse filtro.</Typography>
        ) : (
          dados.map((d) => (
            <Box key={d.nome} sx={{ mb: 1.8 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a2e', mb: 0.5 }}>{d.nome}</Typography>
              {d.contrato > 0 && (
                <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>
                  Contratos {formatarReal(d.contrato)} · recebido {formatarReal(d.recebido)} ({Math.round((d.recebido / d.contrato) * 100)}%) · no mês {formatarReal(d.recebidoMes)}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
                <Typography sx={{ fontSize: 10, color: '#94a3b8', width: 64, flexShrink: 0 }}>Consultoria</Typography>
                <Box sx={{ flex: 1, height: 7, borderRadius: 4, backgroundColor: '#eef0f3', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${Math.min(d.pctConsultoria, 100)}%`, backgroundColor: '#e94560', borderRadius: 4 }} />
                </Box>
                <Typography sx={{ fontSize: 10.5, fontFamily: 'monospace', color: '#64748b', width: 36, textAlign: 'right' }}>{Math.round(d.pctConsultoria)}%</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Typography sx={{ fontSize: 10, color: '#94a3b8', width: 64, flexShrink: 0 }}>Workshop</Typography>
                <Box sx={{ flex: 1, height: 7, borderRadius: 4, backgroundColor: '#eef0f3', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${Math.min(d.pctWorkshop, 100)}%`, backgroundColor: '#2563EB', borderRadius: 4 }} />
                </Box>
                <Typography sx={{ fontSize: 10.5, fontFamily: 'monospace', color: '#64748b', width: 36, textAlign: 'right' }}>{Math.round(d.pctWorkshop)}%</Typography>
              </Box>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}

export default function ControleCarga() {
  const { temAcesso } = useAuth();
  const acessoLiberado = temAcesso('controle_carga');
  // Contratos com vários projetos (Fase 3): enquanto o módulo "contratos" não é ligado para todos,
  // só quem o tem enxerga e conta esses contratos.
  const usaContratos = true;   // v1.17.0: contratos liberado para todos
  const [projetos, setProjetos] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [membros, setMembros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erros, setErros] = useState([]);

  const [filtroCelula, setFiltroCelula] = useState('');
  const [filtroRisco, setFiltroRisco] = useState('');
  const [filtroProjeto, setFiltroProjeto] = useState('');
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());

  useEffect(() => {
    if (!acessoLiberado) { setLoading(false); return; }
    // allSettled: cada pedido é independente. Se um falhar (ex.: o histórico),
    // os outros continuam aparecendo e a tela avisa qual parte falhou.
    const buscas = [
      api.get('/projetos/'),
      api.get('/membros-equipe/'),
    ];
    if (usaContratos) buscas.push(api.get('/contratos/controle-carga/'));
    Promise.allSettled(buscas).then(([proj, mem, con]) => {
      const falhas = [];
      // Projeto de contrato novo não entra aqui: a carga dele é do contrato (seção "Contratos com vários projetos").
      if (proj.status === 'fulfilled') {
        setProjetos(proj.value.data.filter((p) => p.status === 'em_andamento' && !(usaContratos && p.contrato_novo_id)));
      } else falhas.push('projetos');
      if (usaContratos) {
        if (con && con.status === 'fulfilled') setContratos(con.value.data);
        else falhas.push('contratos');
      }
      if (mem.status === 'fulfilled') setMembros(mem.value.data);
      else falhas.push('membros da equipe');
      setErros(falhas);
      setLoading(false);
    });
  }, [acessoLiberado, usaContratos]);

  if (!acessoLiberado) {
    return (
      <AcessoNegado mensagem="Você não tem acesso ao Controle de Carga. Fale com o Admin se precisar desse acesso." />
    );
  }
  if (loading) return <Carregando />;

  // Célula de cada membro da equipe, pra saber a quais células um projeto pertence
  const celulaPorMembro = {};
  membros.forEach((c) => { celulaPorMembro[c.id] = c.celula; });

  const celulasDoProjeto = (projeto) => {
    const ids = new Set();
    (projeto.membros_equipe || []).forEach((membroId) => {
      const cel = celulaPorMembro[membroId];
      if (cel) ids.add(cel.id);
    });
    return ids;
  };

  const celulasMap = {};
  membros.forEach((c) => {
    if (c.celula && !celulasMap[c.celula.id]) celulasMap[c.celula.id] = c.celula;
  });
  contratos.forEach((c) => {
    if (c.celula_id && !celulasMap[c.celula_id]) celulasMap[c.celula_id] = { id: c.celula_id, nome: c.celula_nome };
  });
  const celulas = Object.values(celulasMap).sort((a, b) => a.nome.localeCompare(b.nome));

  const projetosComRisco = projetos.map((p) => ({ ...p, risco: calcularRisco(p), celulaIds: celulasDoProjeto(p) }));

  // Filtro de célula — afeta os anéis, os quadradinhos e a distribuição de risco
  const projetosDaCelula = filtroCelula
    ? projetosComRisco.filter((p) => p.celulaIds.has(filtroCelula))
    : projetosComRisco;

  // Contratos novos que já têm projeto em andamento entram nos totais pelo CONTRATO (a carga é um pool
  // único; somar pelos projetos contaria em dobro). Contratos aguardando projeto/início aparecem só na seção própria.
  const contratosEmAndamento = contratos.filter((c) => c.situacao === 'em_andamento');
  const contratosDaCelula = filtroCelula ? contratosEmAndamento.filter((c) => c.celula_id === filtroCelula) : contratosEmAndamento;
  const contratosListados = filtroCelula ? contratos.filter((c) => c.celula_id === filtroCelula || !c.celula_id) : contratos;

  // Filtro de projeto (v1.26.0): lista só os projetos em andamento que cabem na célula escolhida.
  // Projeto de contrato novo aparece como "Contrato › Projeto"; escolher um deles mostra o CONTRATO inteiro,
  // porque a carga é um pool único do contrato (não dá para separar a carga de um projeto só).
  const opcoesProjeto = [
    ...projetosDaCelula.map((p) => ({ chave: `p-${p.id}`, rotulo: p.nome, contratoId: null })),
    ...contratosDaCelula.flatMap((c) => c.projetos
      .filter((p) => p.status === 'em_andamento')
      .map((p) => ({ chave: `c-${c.id}-${p.id}`, rotulo: `${c.nome} › ${p.nome}`, contratoId: c.id }))),
  ].sort((a, b) => a.rotulo.localeCompare(b.rotulo));
  // Se a célula mudou e o projeto escolhido não está mais na lista, volta para "Todos".
  const opcaoProjeto = opcoesProjeto.find((o) => o.chave === filtroProjeto) || null;
  const projetosPorCelula = opcaoProjeto
    ? projetosDaCelula.filter((p) => `p-${p.id}` === opcaoProjeto.chave)
    : projetosDaCelula;
  const contratosPorCelula = opcaoProjeto
    ? contratosDaCelula.filter((c) => c.id === opcaoProjeto.contratoId)
    : contratosDaCelula;
  const contratosVisiveis = opcaoProjeto
    ? contratosListados.filter((c) => c.id === opcaoProjeto.contratoId)
    : contratosListados;
  const somaContratos = (lista, campo) => lista.reduce((acc, c) => acc + Number(c[campo] || 0), 0);
  const recebidoDoContratoNoMes = (c) => Number((c.recebido_por_mes || {})[`${ano}-${String(mes).padStart(2, '0')}`] || 0);
  // Com um projeto de contrato escolhido, conta só ele (a carga continua sendo a do contrato todo).
  const projetosAtivosDosContratos = opcaoProjeto && opcaoProjeto.contratoId
    ? 1
    : contratosPorCelula.reduce((acc, c) => acc + c.projetos.filter((p) => p.status === 'em_andamento').length, 0);

  const totalContratadaConsultoria = projetosPorCelula.reduce((acc, p) => acc + Number(p.carga_total_contratada || 0), 0)
    + somaContratos(contratosPorCelula, 'carga_total');
  const totalRealizadaConsultoria = projetosPorCelula.reduce((acc, p) => acc + Number(p.realizado_acumulado || 0), 0)
    + somaContratos(contratosPorCelula, 'carga_usada');
  const totalWorkshopsVendidos = projetosPorCelula.reduce((acc, p) => acc + Number(p.workshops_vendidos || 0), 0)
    + somaContratos(contratosPorCelula, 'workshops_vendidos');
  const totalWorkshopsRealizados = projetosPorCelula.reduce((acc, p) => acc + Number(p.workshops_realizados || 0), 0)
    + somaContratos(contratosPorCelula, 'workshops_realizados');

  const totalContratos = projetosPorCelula.reduce((acc, p) => acc + (temContrato(p) ? Number(p.valor_contrato) : 0), 0)
    + somaContratos(contratosPorCelula, 'valor_contrato');
  const totalRecebido = projetosPorCelula.reduce((acc, p) => acc + (temContrato(p) ? recebidoAcumulado(p) : 0), 0)
    + somaContratos(contratosPorCelula, 'recebido_total');
  const totalRecebidoMes = projetosPorCelula.reduce((acc, p) => acc + (temContrato(p) ? recebidoNoMes(p, mes, ano) : 0), 0)
    + contratosPorCelula.reduce((acc, c) => acc + recebidoDoContratoNoMes(c), 0);
  const pctContratoRecebido = totalContratos > 0 ? (totalRecebido / totalContratos) * 100 : 0;

  const pctConsultoriaGeral = totalContratadaConsultoria > 0 ? (totalRealizadaConsultoria / totalContratadaConsultoria) * 100 : 0;
  const pctWorkshopGeral = totalWorkshopsVendidos > 0 ? (totalWorkshopsRealizados / totalWorkshopsVendidos) * 100 : 0;

  const resumo = {
    ritmo: projetosPorCelula.filter((p) => p.risco.nivel === 'ritmo').length + contratosPorCelula.filter((c) => c.risco === 'ritmo').length,
    atencao: projetosPorCelula.filter((p) => p.risco.nivel === 'atencao').length + contratosPorCelula.filter((c) => c.risco === 'atencao').length,
    critico: projetosPorCelula.filter((p) => p.risco.nivel === 'critico').length + contratosPorCelula.filter((c) => c.risco === 'critico').length,
  };
  const totalNaDistribuicao = projetosPorCelula.length + contratosPorCelula.length;

  // Filtro de risco — afeta só a comparação entre células
  const dadosComparacaoCelulas = celulas.map((cel) => {
    const projetosDaCelula = projetosComRisco.filter((p) => p.celulaIds.has(cel.id));
    const considerados = filtroRisco ? projetosDaCelula.filter((p) => p.risco.nivel === filtroRisco) : projetosDaCelula;
    // Contratos novos pertencem à célula do contrato (todos os projetos dele seguem essa célula).
    const doContrato = contratosEmAndamento.filter((c) => c.celula_id === cel.id && (!filtroRisco || c.risco === filtroRisco));
    const contratada = considerados.reduce((acc, p) => acc + Number(p.carga_total_contratada || 0), 0) + somaContratos(doContrato, 'carga_total');
    const realizada = considerados.reduce((acc, p) => acc + Number(p.realizado_acumulado || 0), 0) + somaContratos(doContrato, 'carga_usada');
    const vendidos = considerados.reduce((acc, p) => acc + Number(p.workshops_vendidos || 0), 0) + somaContratos(doContrato, 'workshops_vendidos');
    const realizados = considerados.reduce((acc, p) => acc + Number(p.workshops_realizados || 0), 0) + somaContratos(doContrato, 'workshops_realizados');
    const contrato = considerados.reduce((acc, p) => acc + (temContrato(p) ? Number(p.valor_contrato) : 0), 0) + somaContratos(doContrato, 'valor_contrato');
    const recebido = considerados.reduce((acc, p) => acc + (temContrato(p) ? recebidoAcumulado(p) : 0), 0) + somaContratos(doContrato, 'recebido_total');
    const recebidoMes = considerados.reduce((acc, p) => acc + (temContrato(p) ? recebidoNoMes(p, mes, ano) : 0), 0)
      + doContrato.reduce((acc, c) => acc + recebidoDoContratoNoMes(c), 0);
    return {
      nome: cel.nome,
      contrato,
      recebido,
      recebidoMes,
      pctConsultoria: contratada > 0 ? (realizada / contratada) * 100 : 0,
      pctWorkshop: vendidos > 0 ? (realizados / vendidos) * 100 : 0,
    };
  }).filter((d) => d.pctConsultoria > 0 || d.pctWorkshop > 0);

  return (
    <Box>
      <PageHeader titulo="Controle de Carga" />

      {erros.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Não foi possível carregar: {erros.join(', ')}. Os números abaixo podem estar incompletos.
          Atualize a página; se continuar, avise o TI.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
        <TextField select size="small" label="Célula" value={filtroCelula} onChange={(e) => setFiltroCelula(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">Todas</MenuItem>
          {celulas.map((c) => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Projeto" value={opcaoProjeto ? opcaoProjeto.chave : ''}
          onChange={(e) => setFiltroProjeto(e.target.value)} sx={{ minWidth: 220, maxWidth: 360 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {opcoesProjeto.map((o) => <MenuItem key={o.chave} value={o.chave}>{o.rotulo}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Risco" value={filtroRisco} onChange={(e) => setFiltroRisco(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">Todos</MenuItem>
          {Object.entries(RISCO_CONFIG).map(([chave, cfg]) => <MenuItem key={chave} value={chave}>{cfg.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Mês" value={mes} onChange={(e) => setMes(Number(e.target.value))} sx={{ minWidth: 130 }}>
          {NOMES_MES.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Ano" type="number" value={ano} onChange={(e) => setAno(Number(e.target.value))} sx={{ width: 100 }} />
      </Box>

      <Box sx={{
        position: 'relative', borderRadius: 3, overflow: 'hidden', mb: 3,
        background: 'linear-gradient(135deg, #13375F 0%, #0d2642 100%)',
        backgroundImage: `
          repeating-linear-gradient(45deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 26px),
          linear-gradient(135deg, #13375F 0%, #0d2642 100%)
        `,
        p: { xs: 2.5, sm: 4 },
      }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
          <AnelGrande pct={pctConsultoriaGeral} label="Consultoria" cor="#e94560" />
          <AnelGrande pct={pctWorkshopGeral} label="Workshop" cor="#2563EB" />

          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '0.06em' }}>Projetos ativos</Typography>
                <Typography sx={{ color: 'white', fontFamily: 'monospace', fontSize: 22, fontWeight: 700 }}>{projetosPorCelula.length + projetosAtivosDosContratos}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '0.06em' }}>Consultoria</Typography>
                <Typography sx={{ color: 'white', fontFamily: 'monospace', fontSize: 18, fontWeight: 700 }}>{formatDias(totalContratadaConsultoria)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '0.06em' }}>Workshop</Typography>
                <Typography sx={{ color: 'white', fontFamily: 'monospace', fontSize: 18, fontWeight: 700 }}>{totalWorkshopsVendidos}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '0.06em' }}>Contratos</Typography>
                <Typography sx={{ color: 'white', fontFamily: 'monospace', fontSize: 18, fontWeight: 700 }}>{formatarReal(totalContratos)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '0.06em' }}>Recebido ({Math.round(pctContratoRecebido)}%)</Typography>
                <Typography sx={{ color: '#16A34A', fontFamily: 'monospace', fontSize: 18, fontWeight: 700 }}>{formatarReal(totalRecebido)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '0.06em' }}>Recebido — {NOMES_MES[mes - 1]}</Typography>
                <Typography sx={{ color: '#16A34A', fontFamily: 'monospace', fontSize: 18, fontWeight: 700 }}>{formatarReal(totalRecebidoMes)}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <BarraDistribuicaoRisco resumo={resumo} total={totalNaDistribuicao} />
        <BarraComparacaoCelulas dados={dadosComparacaoCelulas} />
      </Box>

      {usaContratos && (
        <ContratosCarga contratos={contratosVisiveis} mes={mes} ano={ano} nomeMes={NOMES_MES[mes - 1]} />
      )}

      {!(opcaoProjeto && projetosPorCelula.length === 0) && (
        <ContratoPorProjeto projetos={projetosPorCelula} mes={mes} ano={ano} />
      )}

      <CargaMensalProjetos mes={mes} ano={ano} filtroCelula={filtroCelula} opcaoProjeto={opcaoProjeto} />
    </Box>
  );
}
